import { inject, Injectable, OnDestroy } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, map, Observable, Subject, Subscription, takeUntil, takeWhile, timer } from 'rxjs';
import { DefaultService as RestApiService } from 'src/app/swagger-api/api/default.service';
import { promiseWithCatch } from '../common/app.helpers';
import AppSettings from '../common/app.settings';
import FieldAllowedValueClasses from '../forms/fields/field-allowed-value-classes.values';
import { FieldAttributeConstructorService } from '../forms/fields/field-attributes-constructor.service';
import { FormsAttributeService } from '../forms/forms-attribute.service';
import { FormTypes } from '../forms/forms.enum';
import { FieldAttribute, FormConstructorContent } from '../interfaces/form-and-field.interface';
import { VerifyCodeData } from '../interfaces/query-url-params.interface';
import { NotificationContextAttributesService } from '../notifications/notifications-context-attributes.service';
import { NotificationService } from '../notifications/notifications.service';
import {
  ChannelKind,
  GenerateAuthenticatorSecretResponse,
  OtpAttemptStatus,
  OtpStatusResponse,
  OtpVerificationAttemptStatus,
} from '../swagger-api';
import { phoneNumberMock, verificationCodeMock } from './auth.mock';
import { AuthService } from './auth.service';
import { SessionService } from './session.service';

@Injectable()
export class SecondAuthLevelService implements OnDestroy {
  private secondAuthLvl$: Subject<FormConstructorContent> = new Subject();
  private codeSent$: Subject<boolean> = new Subject();
  private codeCountdownTimer$: Subject<number> = new Subject();
  private verificationAttemptsLimitReached$: Subject<boolean> = new Subject();
  private stopTimer$: Subject<void> = new Subject();
  private timerSubscription$: Subscription;

  private otpStatusResponse$: Subject<OtpStatusResponse> = new Subject();
  private otpAuthenticatorSecret$: Subject<string> = new Subject();
  private authenticatorUnbounded$: Subject<void> = new Subject();

  private apiService = inject(RestApiService);
  private sessionService = inject(SessionService);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private formsAttributeService = inject(FormsAttributeService);
  private attrService = inject(FieldAttributeConstructorService);
  private modalAttributesService = inject(NotificationContextAttributesService);
  private translateService = inject(TranslateService);
  private router = inject(Router);

  public ngOnDestroy(): void {
    if (this.secondAuthLvl$) {
      this.secondAuthLvl$.complete();
    }
    if (this.codeSent$) {
      this.codeSent$.complete();
    }

    if (this.otpAuthenticatorSecret$) {
      this.otpAuthenticatorSecret$.complete();
    }

    if (this.timerSubscription$) {
      this.timerSubscription$.unsubscribe();
    }

    if (this.authenticatorUnbounded$) {
      this.authenticatorUnbounded$.complete();
    }
  }

  public get authenticatorUnbounded(): Observable<void> {
    return this.authenticatorUnbounded$.asObservable();
  }

  public get secondAuthLvlContent(): Observable<FormConstructorContent> {
    return this.secondAuthLvl$.asObservable();
  }

  public get codeSent(): Observable<boolean> {
    return this.codeSent$.asObservable();
  }

  public get otpStatusResponse(): Observable<OtpStatusResponse> {
    return this.otpStatusResponse$.asObservable();
  }

  public get otpAuthenticatorSecret(): Observable<string> {
    return this.otpAuthenticatorSecret$.asObservable();
  }

  public get codeCountdownTimer(): Observable<number> {
    return this.codeCountdownTimer$.asObservable();
  }

  public get verificationAttemptsLimitReached(): Observable<boolean> {
    return this.verificationAttemptsLimitReached$.asObservable();
  }

  public get verificationCodeValidators(): ValidatorFn[] {
    return this.attrService.formateValidators(verificationCodeMock);
  }

  public fetchSecondFormAuthForm(changePhone = false): void {
    this.secondAuthLvl$.next({
      formAttribute: this.formsAttributeService.getFormAttribute(FormTypes.SECOND_AUTH_LEVEL),
      fieldAttributes: [this.getPhoneNumberAttr(changePhone)],
      data: {
        phone_number: this.sessionService.user.profile.phone_number || '',
        email: this.sessionService.user.profile.verified_email || this.sessionService.user.profile.email || '',
      },
    });
  }

  public getOtpStatus(): void {
    this.apiService
      .restAuthRequestOneTimeCodeGet()
      .pipe(catchError(() => EMPTY))
      .subscribe((otpStatus: OtpStatusResponse) => {
        this.otpStatusResponse$.next(otpStatus);

        if (otpStatus.request_otp_blocked_until) {
          this.setCodeRequestTimer(otpStatus.request_otp_blocked_until);
        } else {
          this.removeRequestOtpBlockedUntilLocalStorage();
        }
      });
  }

  public removeRequestOtpBlockedUntilLocalStorage(): void {
    localStorage.removeItem(AppSettings.LOCALSTORE_KEY_REQUEST_OTP_BLOCKED_UNTIL);
  }

  public setRequestOtpBlockedUntilLocalStorage(requestOtpBlockedUntil: Date): void {
    localStorage.setItem(AppSettings.LOCALSTORE_KEY_REQUEST_OTP_BLOCKED_UNTIL, JSON.stringify(requestOtpBlockedUntil));
  }

  public sendCode(channelKind: ChannelKind, phoneNumber?: string): void {
    this.apiService.restAuthRequestOneTimeCodePost({ phone_number: phoneNumber, channel_kind: channelKind }).subscribe({
      next: (otpStatus: OtpStatusResponse) => {
        this.codeSent$.next(true);
        this.setCodeRequestTimer(otpStatus.request_otp_blocked_until);
      },
      error: () => {
        this.codeSent$.next(false);
      },
    });
  }

  public generateAuthenticatorSecret(previousPage: string): void {
    this.apiService.restAuthAuthenticatorPost().subscribe({
      next: (authenticatorSecretResponse: GenerateAuthenticatorSecretResponse) => {
        this.emitAuthenticatorSecretResponse(authenticatorSecretResponse.secret);
      },
      error: () => {
        promiseWithCatch(this.router.navigateByUrl(previousPage));
      },
    });
  }

  public emitAuthenticatorSecretResponse(secret: string): void {
    this.otpAuthenticatorSecret$.next(secret);
  }

  public resetPhoneNumberInput(): void {
    this.codeSent$.next(false);
  }

  public verifyCode(data: VerifyCodeData): void {
    this.apiService
      .restAuthVerifyOneTimeCodePost({
        code: data.code,
        phone_number: data.phoneNumber,
        channel_kind: data.channelKind,
      })
      .pipe(catchError(() => EMPTY))
      .subscribe((verificationResult: OtpAttemptStatus) => {
        if (verificationResult.attempt_status === OtpVerificationAttemptStatus.Success) {
          this.sessionService.is2AuthLvlPassed = true;
          this.stopCodeRequestTimer();
          this.showSuccessMessage(data);

          // Before there was a conditional, that checked if it should call refetchUserDataAfter2FA or
          // navigateByQueryParams, but inside the refetchUserDataAfter2FA
          // method there is a 'navigateByQueryParams' call so it is the same
          this.authService.refetchUserDataAfter2FA(data.redirectPage, data.params);
        }
        if (verificationResult.attempt_status === OtpVerificationAttemptStatus.CodeMismatch) {
          this.emitVerificationAttemptsLimitReached(verificationResult.verification_attempts_limit_reached);

          if (verificationResult.request_otp_blocked_until) {
            this.resetPhoneNumberInput();
            this.setCodeRequestTimer(verificationResult.request_otp_blocked_until);
            this.notificationService.dialogDefault({
              title: 'OTP_TOO_MANY_ATTEMPTS_ERROR_TITLE',
              description: 'OTP_TOO_MANY_ATTEMPTS_ERROR_DESCRIPTION',
              buttons: [this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT', 'btn-primary')],
            });
          } else {
            this.notificationService.dialogDefault({
              title: 'OTP_CODE_MISSMATCH_ERROR_TITLE',
              description: 'OTP_CODE_MISSMATCH_ERROR_DESCRIPTION',
              buttons: [this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT', 'btn-primary')],
            });
          }
        }
      });
  }

  public unbindAuthenticator(): void {
    this.apiService
      .restAuthAuthenticatorDelete()
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.emitAuthenticatorUnbounded();
      });
  }

  public emitAuthenticatorUnbounded(): void {
    this.authenticatorUnbounded$.next();
  }

  public setCodeRequestTimer(otpRequestBlockedUntil: Date): void {
    const currentDate: Date = this.getCurrentDate();
    const otpRequestBlockedUntilDate: Date = new Date(otpRequestBlockedUntil);
    const seconds: number = (otpRequestBlockedUntilDate.getTime() - currentDate.getTime()) / 1000;

    this.stopCodeRequestTimer();
    if (seconds < 0) {
      this.codeCountdownTimer$.next(0);
    } else {
      this.setBlockedCountdownTimer(seconds);
      this.setRequestOtpBlockedUntilLocalStorage(otpRequestBlockedUntil);
    }
  }

  public emitVerificationAttemptsLimitReached(verificationAttemptsLimitReached: boolean): void {
    this.verificationAttemptsLimitReached$.next(verificationAttemptsLimitReached);
  }

  public getCurrentDate(): Date {
    return new Date();
  }

  public setBlockedCountdownTimer(seconds: number): void {
    this.timerSubscription$ = timer(0, 1000)
      .pipe(
        map((n: number) => (seconds - n) * 1000),
        takeWhile((n: number): boolean => n >= 0),
        takeUntil(this.stopTimer$),
      )
      .subscribe((countdownSec: number): void => {
        this.codeCountdownTimer$.next(countdownSec);
      });
  }

  public stopCodeRequestTimer(): void {
    if (this.timerSubscription$) {
      this.removeRequestOtpBlockedUntilLocalStorage();
      this.stopTimer$.next();
      this.timerSubscription$.unsubscribe();
    }
  }

  public showChangePhoneBeforeEditingMessage(): void {
    this.notificationService.dialogDefault({
      title: 'PHONE_NUMBER_ALREADY_SAVED_TITLE',
      description: 'PHONE_NUMBER_ALREADY_SAVED_DESCRIPTION',
      buttons: [this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT')],
    });
  }

  private showSuccessMessage(data: VerifyCodeData): void {
    if (data.phoneNumber) {
      this.notificationService.dialogDefault({
        title: 'PHONE_NUMBER_VERIFIED_TITLE',
        description: this.translateService.instant('PHONE_NUMBER_VERIFIED_DESCRIPTION', {
          number: data.phoneNumber,
        }) as string,
        buttons: [this.modalAttributesService.createDialogButton('BTN_OK_HINT', 'btn-primary')],
      });
    } else if (data.bindAuthenticator) {
      this.notificationService.dialogDefault({
        title: 'SECOND_AUTH_LVL_AUTHENTICATOR_VERIFIED_TITLE',
        description: 'SECOND_AUTH_LVL_AUTHENTICATOR_VERIFIED_DESCRIPTION',
        buttons: [this.modalAttributesService.createDialogButton('BTN_OK_HINT', 'btn-primary')],
      });
    }
  }

  private getPhoneNumberAttr(changePhone: boolean): FieldAttribute {
    return this.attrService.getFieldAttributes(
      {
        ...phoneNumberMock,
        value_class_front: changePhone ? FieldAllowedValueClasses.PHONE : FieldAllowedValueClasses.PHONE_2FA,
      },
      FormTypes.SECOND_AUTH_LEVEL,
    );
  }
}
